import { Upload, message } from 'antd'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd'
import { Button } from '@/components/ui/Button'

export interface ImageUploadProps {
  /**
   * Current file list
   */
  fileList: UploadFile[]

  /**
   * Callback when file list changes
   */
  onChange: (fileList: UploadFile[]) => void

  /**
   * Callback when file is removed
   */
  onRemove?: () => void

  /**
   * Label for the upload field
   */
  label?: string

  /**
   * Help text shown below the upload
   */
  helpText?: string

  /**
   * Maximum file size in MB
   * @default 2
   */
  maxSizeMB?: number

  /**
   * Accepted file types
   * @default 'image/*'
   */
  accept?: string

  /**
   * Button text when no file is uploaded
   * @default 'Click to Upload'
   */
  buttonText?: string

  /**
   * Whether the field is disabled
   */
  disabled?: boolean

  /**
   * Custom class name for the container
   */
  className?: string

  /**
   * Upload list type
   * @default 'picture'
   */
  listType?: 'text' | 'picture' | 'picture-card' | 'picture-circle'
}

/**
 * ImageUpload Component
 *
 * A reusable image upload field with preview and validation.
 *
 * @example
 * const [fileList, setFileList] = useState<UploadFile[]>([])
 *
 * <ImageUpload
 *   label="Event Logo"
 *   fileList={fileList}
 *   onChange={setFileList}
 *   helpText="Maximum upload file size: 2 MB"
 * />
 *
 * @example
 * // With existing image
 * const [fileList, setFileList] = useState<UploadFile[]>([
 *   { uid: '-1', name: 'image.png', status: 'done', url: 'https://example.com/image.png' }
 * ])
 *
 * <ImageUpload
 *   label="Event Logo"
 *   fileList={fileList}
 *   onChange={setFileList}
 *   onRemove={() => setRemoveFlag(true)}
 * />
 */
export function ImageUpload({
  fileList,
  onChange,
  onRemove,
  label,
  helpText,
  maxSizeMB = 2,
  accept = 'image/*',
  buttonText = 'Click to Upload',
  disabled = false,
  className = '',
  listType = 'picture',
}: ImageUploadProps) {
  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    onChange(newFileList)
  }

  const handleRemove = () => {
    onChange([])
    onRemove?.()
  }

  const beforeUpload = (file: File) => {
    const isValidSize = file.size / 1024 / 1024 < maxSizeMB
    if (!isValidSize) {
      message.error(`File must be smaller than ${maxSizeMB}MB`)
      return Upload.LIST_IGNORE
    }
    return false // Prevent automatic upload
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      <Upload
        listType={listType}
        fileList={fileList}
        onChange={handleChange}
        beforeUpload={beforeUpload}
        maxCount={1}
        accept={accept}
        onRemove={handleRemove}
        disabled={disabled}
      >
        {fileList.length === 0 && (
          <Button variant="secondary" icon={<UploadOutlined />} disabled={disabled}>
            {buttonText}
          </Button>
        )}
      </Upload>
      {helpText && (
        <p className="text-xs text-gray-500 mt-1">{helpText}</p>
      )}
    </div>
  )
}

export interface ImageUploadWithPreviewProps extends Omit<ImageUploadProps, 'listType'> {
  /**
   * Preview image URL (for existing images)
   */
  previewUrl?: string | null

  /**
   * Alt text for preview image
   */
  previewAlt?: string

  /**
   * Aspect ratio for the preview container
   * @default 'auto'
   */
  aspectRatio?: 'auto' | 'square' | '16:9' | '4:3'
}

/**
 * ImageUploadWithPreview Component
 *
 * An image upload field with a prominent preview area.
 *
 * @example
 * <ImageUploadWithPreview
 *   label="Event Banner"
 *   fileList={bannerFile}
 *   onChange={setBannerFile}
 *   previewUrl={event.banner_url}
 *   aspectRatio="16:9"
 * />
 */
export function ImageUploadWithPreview({
  fileList,
  onChange,
  onRemove,
  label,
  helpText,
  maxSizeMB = 2,
  accept = 'image/*',
  buttonText = 'Click to Upload',
  disabled = false,
  className = '',
  previewUrl,
  previewAlt = 'Preview',
  aspectRatio = 'auto',
}: ImageUploadWithPreviewProps) {
  const hasFile = fileList.length > 0
  const currentPreviewUrl = hasFile && fileList[0].url
    ? fileList[0].url
    : hasFile && fileList[0].originFileObj
      ? URL.createObjectURL(fileList[0].originFileObj)
      : previewUrl

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    onChange(newFileList)
  }

  const handleRemove = () => {
    onChange([])
    onRemove?.()
  }

  const beforeUpload = (file: File) => {
    const isValidSize = file.size / 1024 / 1024 < maxSizeMB
    if (!isValidSize) {
      message.error(`File must be smaller than ${maxSizeMB}MB`)
      return Upload.LIST_IGNORE
    }
    return false
  }

  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case 'square':
        return 'aspect-square'
      case '16:9':
        return 'aspect-video'
      case '4:3':
        return 'aspect-[4/3]'
      default:
        return ''
    }
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {currentPreviewUrl ? (
        <div className="space-y-2">
          <div className={`relative rounded-lg overflow-hidden border border-gray-200 ${getAspectRatioClass()}`}>
            <img
              src={currentPreviewUrl}
              alt={previewAlt}
              className="w-full h-full object-cover"
            />
            {!disabled && (
              <button
                type="button"
                onClick={handleRemove}
                className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                aria-label="Remove image"
              >
                <DeleteOutlined className="text-red-500" />
              </button>
            )}
          </div>
          {!disabled && (
            <Upload
              showUploadList={false}
              beforeUpload={beforeUpload}
              onChange={handleChange}
              accept={accept}
              disabled={disabled}
            >
              <Button variant="secondary" size="small">
                Replace Image
              </Button>
            </Upload>
          )}
        </div>
      ) : (
        <Upload
          listType="picture-card"
          fileList={[]}
          onChange={handleChange}
          beforeUpload={beforeUpload}
          maxCount={1}
          accept={accept}
          disabled={disabled}
          className="upload-large"
        >
          <div className="flex flex-col items-center justify-center p-4">
            <UploadOutlined className="text-2xl text-gray-400 mb-2" />
            <span className="text-sm text-gray-500">{buttonText}</span>
          </div>
        </Upload>
      )}

      {helpText && (
        <p className="text-xs text-gray-500 mt-1">{helpText}</p>
      )}
    </div>
  )
}
